import React, { useMemo, useCallback } from "react";
import { Button } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import AuthPageLayout from "../../../components/layout/AuthPageLayout";
import { Form } from "../../../components/Form";
import FormTextInput from "../../../components/Form/FormTextInput";
import FormFieldErrors from "../../../components/Form/FormFieldErrors";
import {
  FormDataType,
  FormFieldValue,
} from "../../../components/Form/FormContext";
import { resetPassword } from "../../../store/actions/authActions";
import { ROUTES } from "../../../constants";
import { validatePassword } from "../../../utils/passwordValidation";
import { useApiErrorHandling } from "../../../hooks";
import {
  CreatePasswordContainer,
  CreatePasswordTitle,
  CreatePasswordSubtitle,
  CreatePasswordActions,
} from "./CreatePasswordPage.styled";

interface CreatePasswordFormFieldsProps {
  apiErrors: string[];
  fieldErrors: { [k: string]: string[] };
  onGoToLogin: () => void;
  isLoading: boolean;
}

const CreatePasswordFormFields: React.FC<CreatePasswordFormFieldsProps> = ({
  apiErrors,
  fieldErrors,
  onGoToLogin,
  isLoading,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <FormTextInput
        name="password"
        label={t("createPassword.password")}
        type="password"
        placeholder={t("createPassword.passwordPlaceholder")}
        required
      />

      <FormTextInput
        name="confirmPassword"
        label={t("createPassword.confirmPassword")}
        type="password"
        placeholder={t("createPassword.confirmPasswordPlaceholder")}
        required
      />

      <FormFieldErrors apiErrors={apiErrors} fieldErrors={fieldErrors} />

      <CreatePasswordActions>
        <Button variant="outlined" onClick={onGoToLogin}>
          {t("createPassword.goToLogin")}
        </Button>

        <Button type="submit" variant="contained" disabled={isLoading}>
          {isLoading
            ? t("createPassword.creating")
            : t("createPassword.confirm")}
        </Button>
      </CreatePasswordActions>
    </>
  );
};

const CreatePasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { apiErrors, fieldErrors, clearErrors, handleApiError } =
    useApiErrorHandling({
      defaultErrorMessage: "Failed to reset password. Please try again.",
    });
  const [isLoading, setIsLoading] = React.useState(false);

  const validationRules = useMemo(
    () => ({
      password: {
        required: true,
        custom: (value: FormFieldValue) => {
          const strValue = String(value || "");
          if (!strValue) {
            return t("createPassword.validation.passwordRequired");
          }
          const error = validatePassword(strValue, t);
          return error
            ? t("createPassword.validation.passwordMinLength")
            : null;
        },
      },
      confirmPassword: {
        required: true,
        custom: (value: FormFieldValue, formData?: FormDataType) => {
          const strValue = String(value || "");
          if (!strValue) {
            return t("createPassword.validation.confirmPasswordRequired");
          }
          if (formData && strValue !== String(formData.password || "")) {
            return t("createPassword.validation.passwordsDoNotMatch");
          }
          return null;
        },
      },
    }),
    [t]
  );

  const handleSubmit = useCallback(
    async (formData: FormDataType) => {
      if (!token) {
        handleApiError({
          message:
            "Reset token is missing. Please try the password reset process again.",
        });
        return;
      }

      clearErrors();
      setIsLoading(true);

      try {
        await dispatch(
          resetPassword({
            token,
            password: String(formData.password),
            passwordConfirm: String(formData.confirmPassword),
          })
        ).unwrap();
        navigate(ROUTES.SUCCESS);
      } catch (error: unknown) {
        handleApiError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, navigate, token, clearErrors, handleApiError]
  );

  const handleGoToLogin = () => {
    navigate(ROUTES.LOGIN);
  };

  return (
    <AuthPageLayout>
      <CreatePasswordContainer>
        <CreatePasswordTitle>{t("createPassword.title")}</CreatePasswordTitle>

        <CreatePasswordSubtitle>
          {t("createPassword.subtitle")}
        </CreatePasswordSubtitle>

        <Form
          validationRules={validationRules}
          onSubmit={handleSubmit}
          showFieldErrors={false}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            width: "100%",
          }}
        >
          <CreatePasswordFormFields
            apiErrors={apiErrors}
            fieldErrors={fieldErrors}
            onGoToLogin={handleGoToLogin}
            isLoading={isLoading}
          />
        </Form>
      </CreatePasswordContainer>
    </AuthPageLayout>
  );
};

export default CreatePasswordPage;
